'use client';

import {
  Building,
  CheckCircle,
  AlertTriangle as ExclamationTriangleIcon,
  User,
} from 'lucide-react';
import React from 'react';

import { applyCpfCnpjMask, formatCpfCnpj } from '@/shared/utils/cnpj';
import { isCnpj } from '@/shared/utils/documentValidation';
import { Parte } from '@/types';

interface ParteFormProps {
  parte: Parte;
  index: number;
  tipo: 'requerente' | 'requerido';
  onChange: (index: number, campo: keyof Parte, valor: string) => void;
  readOnlyFields?: (keyof Parte)[];
}

const ParteForm: React.FC<ParteFormProps> = ({
  parte,
  index,
  tipo,
  onChange,
  readOnlyFields = [],
}) => {
  const isReadOnly = (campo: keyof Parte) => readOnlyFields.includes(campo);

  const isCNPJ = isCnpj(parte.cpf_cnpj);

  const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = applyCpfCnpjMask(e.target.value);
    onChange(index, 'cpf_cnpj', maskedValue);
  };

  const isRequeridoCompleto =
    tipo === 'requerido' &&
    parte.profissao &&
    parte.profissao.trim() !== '' &&
    parte.estado_civil &&
    parte.estado_civil.trim() !== '' &&
    parte.nacionalidade &&
    parte.nacionalidade.trim() !== '';

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-lg border border-solid border-neutral-200 bg-neutral-50 p-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          {isCNPJ ? (
            <Building className="text-subtext-color h-4 w-4" />
          ) : (
            <User className="text-subtext-color h-4 w-4" />
          )}
          <span className="text-body-bold font-body-bold text-neutral-800">
            {tipo === 'requerente' ? 'Solicitante' : 'Parte contrária'} {index + 1}
          </span>
        </div>

        {tipo === 'requerente' && (
          <div className="border-success-200 bg-success-50 flex items-center gap-1 rounded-full border border-solid px-2 py-1">
            <CheckCircle className="text-success-600 h-3 w-3" />
            <span className="text-caption font-caption text-success-700">Completo</span>
          </div>
        )}

        {tipo === 'requerido' && (
          <div
            className={`flex items-center gap-1 rounded-full border border-solid px-2 py-1 ${
              isRequeridoCompleto
                ? 'border-success-200 bg-success-50'
                : 'border-warning-200 bg-warning-50'
            }`}
          >
            {isRequeridoCompleto ? (
              <>
                <CheckCircle className="text-success-600 h-3 w-3" />
                <span className="text-caption font-caption text-success-700">Completo</span>
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="text-warning-600 h-3 w-3" />
                <span className="text-caption font-caption text-warning-700">Pendente</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body-bold font-body-bold text-neutral-800">
            Nome {isCNPJ ? 'da Empresa' : 'Completo'}
          </span>
          {tipo === 'requerido' ? (
            <div className="text-body font-body text-subtext-color w-full cursor-not-allowed rounded-lg border border-solid border-neutral-200 bg-neutral-100 px-3 py-2">
              {parte.nome || (isCNPJ ? 'Nome da empresa' : 'Nome completo')}
            </div>
          ) : (
            <input
              type="text"
              value={parte.nome}
              onChange={(e) => onChange(index, 'nome', e.target.value)}
              className="text-body font-body focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-solid border-neutral-200 bg-white px-3 py-2 text-neutral-800 focus:ring-1 focus:outline-none"
              placeholder={isCNPJ ? 'Nome da empresa' : 'Nome completo'}
              disabled={tipo === 'requerente' || isReadOnly('nome')}
            />
          )}
        </div>

        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body-bold font-body-bold text-neutral-800">
            {isCNPJ ? 'CNPJ' : 'CPF'}
          </span>
          {tipo === 'requerido' ? (
            <div className="text-body font-body text-subtext-color w-full cursor-not-allowed rounded-lg border border-solid border-neutral-200 bg-neutral-100 px-3 py-2">
              {formatCpfCnpj(parte.cpf_cnpj) || (isCNPJ ? '00.000.000/0000-00' : '000.000.000-00')}
            </div>
          ) : (
            <input
              type="text"
              value={parte.cpf_cnpj}
              onChange={handleCpfCnpjChange}
              className="text-body font-body focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-solid border-neutral-200 bg-white px-3 py-2 text-neutral-800 focus:ring-1 focus:outline-none"
              placeholder={isCNPJ ? '00.000.000/0000-00' : '000.000.000-00'}
              disabled={tipo === 'requerente' || isReadOnly('cpf_cnpj')}
            />
          )}
        </div>

        <div className="flex w-full flex-col items-start gap-2 md:col-span-2">
          <span className="text-body-bold font-body-bold text-neutral-800">Endereço Completo</span>
          {tipo === 'requerido' ? (
            <div className="text-body font-body text-subtext-color w-full cursor-not-allowed rounded-lg border border-solid border-neutral-200 bg-neutral-100 px-3 py-2">
              {parte.endereco || 'Endereço completo com CEP'}
            </div>
          ) : (
            <input
              type="text"
              value={parte.endereco}
              onChange={(e) => onChange(index, 'endereco', e.target.value)}
              className="text-body font-body focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-solid border-neutral-200 bg-white px-3 py-2 text-neutral-800 focus:ring-1 focus:outline-none"
              placeholder="Endereço completo com CEP"
              disabled={tipo === 'requerente' || isReadOnly('endereco')}
            />
          )}
        </div>

        {tipo === 'requerido' && (
          <>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-neutral-800">
                {isCNPJ ? 'Atividade Principal' : 'Profissão'}
              </span>
              <input
                type="text"
                value={parte.profissao}
                onChange={(e) => onChange(index, 'profissao', e.target.value)}
                className="text-body font-body focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-solid border-neutral-200 bg-white px-3 py-2 text-neutral-800 focus:ring-1 focus:outline-none"
                placeholder={isCNPJ ? 'Ramo de atividade' : 'Profissão ou ocupação'}
                disabled={isReadOnly('profissao')}
              />
            </div>

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-neutral-800">
                {isCNPJ ? 'Tipo de Pessoa' : 'Estado Civil'}
              </span>
              <select
                value={parte.estado_civil}
                onChange={(e) => onChange(index, 'estado_civil', e.target.value)}
                className="text-body font-body focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-solid border-neutral-200 bg-white px-3 py-2 text-neutral-800 focus:ring-1 focus:outline-none"
                disabled={isReadOnly('estado_civil')}
              >
                <option value="">Selecione...</option>
                {isCNPJ ? (
                  <>
                    <option value="pessoa_juridica">Pessoa Jurídica</option>
                    <option value="empresa_individual">Empresa Individual</option>
                    <option value="mei">MEI</option>
                  </>
                ) : (
                  <>
                    <option value="solteiro">Solteiro(a)</option>
                    <option value="casado">Casado(a)</option>
                    <option value="divorciado">Divorciado(a)</option>
                    <option value="viuvo">Viúvo(a)</option>
                    <option value="uniao_estavel">União Estável</option>
                  </>
                )}
              </select>
            </div>

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-neutral-800">Nacionalidade</span>
              <input
                type="text"
                value={parte.nacionalidade}
                onChange={(e) => onChange(index, 'nacionalidade', e.target.value)}
                className="text-body font-body focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-solid border-neutral-200 bg-white px-3 py-2 text-neutral-800 focus:ring-1 focus:outline-none"
                placeholder="Nacionalidade"
                disabled={isReadOnly('nacionalidade')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ParteForm;
